import { operatorIdsByFieldType } from '~/evifields/src/operatorIdsByFieldType';
import { getOperatorLabelsByFieldType } from '~/evifields/src/operatorLabelsByFieldType';
import { OperatorId } from '~/evifields/src/types/OperatorId';
import { getOperator } from '~/evifields/src/utils/getOperator';
import { SearchQuery } from '~/features/advanced-search/types';
import { SearchFilter } from '~/types/DashboardV2';
import { FilterQueryEntity } from '~/types/OpenSearch';

import { TicketCompletionType } from './workflowTicketTypes';

/**
 * Type guard to check if an entity has an 'id' property
 * This is needed because SearchQuery is a union type where not all entities have an 'id'
 * @param entity - The entity to check
 * @returns True if the entity has an 'id' property (is a FilterQueryEntity)
 */
export const hasId = (entity: any): entity is FilterQueryEntity => {
  return entity && typeof entity === 'object' && 'id' in entity;
};

/**
 * Helper function to create FilterQueryEntity objects
 * @param id - The field ID
 * @param operator - The operator ID
 * @param valueList - Array of values (defaults to empty array)
 * @param valueMeta - Metadata object (defaults to empty object)
 * @returns FilterQueryEntity object
 */
export const createFilterEntity = (
  id: string,
  operator: OperatorId,
  valueList: any[] = [],
  valueMeta: any = {},
): FilterQueryEntity => ({
  type: 'filter',
  id,
  operator,
  value: {
    value_list: valueList,
    value_meta: valueMeta,
  },
});

/**
 * Generates read-only filters for a specific tab
 * @param activeTab - The current active tab
 * @param userId - The user's ID
 * @param userDisplayName - Optional user display name for UI. If not provided, uses userId
 * @returns Array of read-only filter entities for the tab
 */
export const getReadOnlyFiltersForTab = (
  activeTab: TicketCompletionType,
  userId: string | number,
  userDisplayName?: string,
): SearchQuery => {
  const readOnlyQuery: SearchQuery = [];
  const userIdentifier = userDisplayName || userId;

  switch (activeTab) {
    case TicketCompletionType.NeedsAction:
      readOnlyQuery.push(
        createFilterEntity(
          'assignee_Ticket',
          'contains_any',
          [userIdentifier],
          {
            multi_select_data: {
              is_all_selected: false,
              length: 1,
              search: '',
              selected_values_map: {
                [userId]: true,
              },
            },
          },
        ),
        createFilterEntity('ticket_completed_date_Ticket', 'is_blank'),
        createFilterEntity('ticket_cancelled_date_Ticket', 'is_blank'),
      );
      break;

    case TicketCompletionType.InProgress:
      readOnlyQuery.push(
        createFilterEntity('ticket_completed_date_Ticket', 'is_blank'),
        createFilterEntity('ticket_cancelled_date_Ticket', 'is_blank'),
      );
      break;

    case TicketCompletionType.Cancelled:
      readOnlyQuery.push(
        createFilterEntity('ticket_cancelled_date_Ticket', 'is_not_blank'),
      );
      break;

    case TicketCompletionType.Done:
      readOnlyQuery.push(
        createFilterEntity('ticket_completed_date_Ticket', 'is_not_blank'),
      );
      break;

    case TicketCompletionType.All:
      // No read-only filters for "All" tab
      break;
  }

  return readOnlyQuery;
};

/**
 * Creates a serializer function for user-based filters (assignee, submitter, etc.)
 * This function extracts the display label from filter values for proper rendering
 */
export const createUserFilterSerializer = () => (filter: any) => {
  if (!filter || !filter.values) return '';
  const firstValue = filter.values[0];
  return firstValue?.label || firstValue?.value || '';
};

/**
 * Gets the appropriate operators for a given field type
 * @param fieldType - The type of field (e.g., 'enum', 'date', 'enum_set')
 * @param _intl - Internationalization object (currently unused but kept for consistency)
 * @returns Array of operator objects with id, label, and cardinality
 */
export const getOperatorsForFieldType = (fieldType: string, _intl: any) => {
  const operatorIds =
    operatorIdsByFieldType[fieldType as keyof typeof operatorIdsByFieldType] ||
    [];
  const operatorLabelsByFieldType = getOperatorLabelsByFieldType();
  const operatorLabels =
    operatorLabelsByFieldType[
      fieldType as keyof typeof operatorLabelsByFieldType
    ] || {};

  return operatorIds.map((operatorId) => {
    const operator = getOperator(operatorId);
    return {
      id: operatorId,
      label: operatorLabels[operatorId] || operatorId,
      cardinality: operator?.cardinality || 0,
    };
  });
};

/**
 * Generates the complete list of ticket search filters with proper configuration
 * @param intl - Internationalization object for formatting messages
 * @returns Array of SearchFilter objects configured for ticket filtering
 */
export const getTicketSearchFilters = (intl: any): SearchFilter[] => {
  if (!intl || !intl.formatMessage) {
    // Return filters with default messages if intl is not available
    return [
      {
        id: 'stage_Ticket',
        label: 'Ticket Stage',
        type: 'enum',
        operators: getOperatorsForFieldType('enum', intl),
        section: 'ticket',
        is_chartable: false,
        is_requestable_column: true,
        settings: { options: [] },
      },
      {
        id: 'ticket_submit_date_Ticket',
        label: 'Submitted Date',
        type: 'date',
        operators: getOperatorsForFieldType('date', intl),
        section: 'ticket',
        is_chartable: false,
        is_requestable_column: true,
        settings: {},
      },
      {
        id: 'ticket_completed_date_Ticket',
        label: 'Completed Date',
        type: 'date',
        operators: getOperatorsForFieldType('date', intl),
        section: 'ticket',
        is_chartable: false,
        is_requestable_column: true,
        settings: {},
      },
      {
        id: 'ticket_cancelled_date_Ticket',
        label: 'Canceled Date',
        type: 'date',
        operators: getOperatorsForFieldType('date', intl),
        section: 'ticket',
        is_chartable: false,
        is_requestable_column: true,
        settings: {},
      },
      {
        id: 'submitter_Ticket',
        label: 'Submitter',
        type: 'enum_set',
        operators: getOperatorsForFieldType('enum_set', intl),
        section: 'ticket',
        is_chartable: false,
        is_requestable_column: true,
        settings: { options: [] },
        serialize: createUserFilterSerializer(),
      },
      {
        id: 'assignee_Ticket',
        label: 'Assignee',
        type: 'enum_set',
        operators: getOperatorsForFieldType('enum_set', intl),
        section: 'ticket',
        is_chartable: false,
        is_requestable_column: true,
        settings: { options: [] },
        serialize: createUserFilterSerializer(),
      },
      {
        id: 'participant_Ticket',
        label: 'Participating Users',
        type: 'enum_set',
        operators: getOperatorsForFieldType('enum_set', intl),
        section: 'ticket',
        is_chartable: false,
        is_requestable_column: true,
        settings: { options: [] },
        serialize: createUserFilterSerializer(),
      },
    ];
  }

  return [
    {
      id: 'stage_Ticket',
      label: intl.formatMessage({
        id: 'TicketFilters.ticketStage',
        defaultMessage: 'Ticket Stage',
        description: 'Filter label for ticket stage',
      }),
      type: 'enum',
      operators: getOperatorsForFieldType('enum', intl),
      section: 'ticket',
      is_chartable: false,
      is_requestable_column: true,
      settings: {
        options: [],
      },
    },
    {
      id: 'ticket_submit_date_Ticket',
      label: intl.formatMessage({
        id: 'TicketFilters.submittedDate',
        defaultMessage: 'Submitted Date',
        description: 'Filter label for submitted date',
      }),
      type: 'date',
      operators: getOperatorsForFieldType('date', intl),
      section: 'ticket',
      is_chartable: false,
      is_requestable_column: true,
      settings: {},
    },
    {
      id: 'ticket_completed_date_Ticket',
      label: intl.formatMessage({
        id: 'TicketFilters.completedDate',
        defaultMessage: 'Completed Date',
        description: 'Filter label for completed date',
      }),
      type: 'date',
      operators: getOperatorsForFieldType('date', intl),
      section: 'ticket',
      is_chartable: false,
      is_requestable_column: true,
      settings: {},
    },
    {
      id: 'ticket_cancelled_date_Ticket',
      label: intl.formatMessage({
        id: 'TicketFilters.cancelledDate',
        defaultMessage: 'Canceled Date',
        description: 'Filter label for canceled date',
      }),
      type: 'date',
      operators: getOperatorsForFieldType('date', intl),
      section: 'ticket',
      is_chartable: false,
      is_requestable_column: true,
      settings: {},
    },
    {
      id: 'submitter_Ticket',
      label: intl.formatMessage({
        id: 'TicketFilters.submitter',
        defaultMessage: 'Submitter',
        description: 'Filter label for submitter',
      }),
      type: 'enum_set',
      operators: getOperatorsForFieldType('enum_set', intl),
      section: 'ticket',
      is_chartable: false,
      is_requestable_column: true,
      settings: {
        options: [],
      },
      serialize: createUserFilterSerializer(),
    },
    {
      id: 'assignee_Ticket',
      label: intl.formatMessage({
        id: 'TicketFilters.assignee',
        defaultMessage: 'Assignee',
        description: 'Filter label for assignee',
      }),
      type: 'enum_set',
      operators: getOperatorsForFieldType('enum_set', intl),
      section: 'ticket',
      is_chartable: false,
      is_requestable_column: true,
      settings: {
        options: [],
      },
      serialize: createUserFilterSerializer(),
    },
    {
      id: 'participant_Ticket',
      label: intl.formatMessage({
        id: 'TicketFilters.participatingUsers',
        defaultMessage: 'Participating Users',
        description: 'Filter label for participating users',
      }),
      type: 'enum_set',
      operators: getOperatorsForFieldType('enum_set', intl),
      section: 'ticket',
      is_chartable: false,
      is_requestable_column: true,
      settings: {
        options: [],
      },
      serialize: createUserFilterSerializer(),
    },
  ];
};
